import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Link, Navigate, useParams } from 'react-router-dom';
import '@aws-amplify/ui-react/styles.css';
import { Authenticator, ThemeProvider } from '@aws-amplify/ui-react';
import { Amplify } from 'aws-amplify';
import config from './aws-exports';
import { activeTheme } from './theme';
import ConsideredLoading from './components/ConsideredLoading';
import Layout from './components/Layout';
import { ModalProvider } from './context/ModalContext';
import { MessageProvider } from './context/MessageContext';
import { ColorModeProvider, useColorModeContext } from './context/ColorModeContext';
import ErrorBoundary from './components/common/ErrorBoundary';

const Profile = lazy(() => import('./pages/profile'));
const Design = lazy(() => import('./pages/design'));
const Designs = lazy(() => import('./pages/designs'));
const AdminFormats = lazy(() => import('./pages/formats'));
const AdminFormatNew = lazy(() => import('./pages/formatNew'));
const AdminFormatDetail = lazy(() => import('./pages/formatDetail'));
const AdminListings = lazy(() => import('./pages/listings'));
const AdminListingNew = lazy(() => import('./pages/listingNew'));
const AdminListingDetail = lazy(() => import('./pages/listingDetail'));
const AdminCollections = lazy(() => import('./pages/collections'));
const AdminCollectionNew = lazy(() => import('./pages/collectionNew'));
const AdminCollectionDetail = lazy(() => import('./pages/collectionDetail'));
const AdminHomepage = lazy(() => import('./pages/homepage'));
const OrdersListPage = lazy(() => import('./pages/orders'));
const OrderDetailPage = lazy(() => import('./pages/orderDetail'));

Amplify.configure(config);

function AppInner() {
  const { colorMode } = useColorModeContext();
  useEffect(() => {
    document.documentElement.dataset.geistiColorMode = colorMode;
  }, [colorMode]);
  return (
    <ThemeProvider theme={activeTheme} colorMode={colorMode}>
      <Authenticator
        loginMechanisms={['email']}
        variation="modal"
        hideSignUp
      >
        <ModalProvider>
          <MessageProvider>
            <Suspense fallback={<ConsideredLoading eyebrow="LOADING" voice="One moment." />}>
              <Routes>
                <Route path="/" element={<Layout />}>
                  {/* Root lands on /orders — the daily ops surface — not /designs. */}
                  <Route index element={<Navigate to="/orders" replace />} />
                  <Route path="designs">
                    <Route index element={<Designs />} />
                    <Route path=":designId" element={<Design />} />
                  </Route>
                  <Route path="profile" element={<Profile />} />
                  <Route path="formats" element={<AdminFormats />} />
                  <Route path="formats/new" element={<AdminFormatNew />} />
                  <Route path="formats/:formatId" element={<AdminFormatDetail />} />
                  <Route path="listings" element={<AdminListings />} />
                  <Route path="listings/new" element={<AdminListingNew />} />
                  <Route path="listings/:listingId" element={<AdminListingDetail />} />
                  <Route path="collections" element={<AdminCollections />} />
                  <Route path="collections/new" element={<AdminCollectionNew />} />
                  <Route path="collections/:collectionId" element={<AdminCollectionDetail />} />
                  <Route path="homepage" element={<AdminHomepage />} />
                  <Route path="orders" element={<OrdersListPage />} />
                  <Route path="orders/:shopifyOrderId" element={<OrderDetailPage />} />
                  {/* Bookmark redirects: old /admin/* → /* (subdomain is already admin.). */}
                  <Route path="admin/formats" element={<Navigate to="/formats" replace />} />
                  <Route path="admin/formats/new" element={<Navigate to="/formats/new" replace />} />
                  <Route
                    path="admin/formats/:formatId"
                    element={<RedirectAdmin to="/formats/:formatId" />}
                  />
                  <Route path="admin/listings" element={<Navigate to="/listings" replace />} />
                  <Route path="admin/listings/new" element={<Navigate to="/listings/new" replace />} />
                  <Route
                    path="admin/listings/:listingId"
                    element={<RedirectAdmin to="/listings/:listingId" />}
                  />
                  <Route path="admin/collections" element={<Navigate to="/collections" replace />} />
                  <Route
                    path="admin/collections/new"
                    element={<Navigate to="/collections/new" replace />}
                  />
                  <Route
                    path="admin/collections/:collectionId"
                    element={<RedirectAdmin to="/collections/:collectionId" />}
                  />
                  <Route path="*" element={<NoMatch />} />
                </Route>
              </Routes>
            </Suspense>
          </MessageProvider>
        </ModalProvider>
      </Authenticator>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ColorModeProvider>
        <AppInner />
      </ColorModeProvider>
    </ErrorBoundary>
  );
}

// Redirect old `/admin/<resource>/:id` URLs to `/<resource>/:id`. Kept indefinitely
// so old bookmarks / browser history don't 404 after the URL flattening.
function RedirectAdmin({ to }: { to: string }) {
  const params = useParams();
  let target = to;
  for (const [k, v] of Object.entries(params)) {
    if (typeof v === 'string') target = target.replace(`:${k}`, v);
  }
  return <Navigate to={target} replace />;
}

function NoMatch() {
  return (
    <div>
      <h2>Nothing to see here!</h2>
      <p>
        <Link to="/">Go to the home page</Link>
      </p>
    </div>
  );
}
